import Button from "@/components/common/Button/Button";
import Drawer from "@/components/common/Drawer/Drawer";
import { Input } from "@/components/ui/input";
import { useGeminiChat } from "@/hooks/useGeminiChat";
import { GEMINI_API_KEY } from "@/utils/api/config";
import { GoogleGenerativeAI } from "@google/generative-ai";
import { KeyboardEvent, useEffect, useState } from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { SparklesIcon, UserIcon } from "@heroicons/react/24/outline";

enum ChatSource {
	USER = "user",
	GEMINI = "gemini",
}

type GeminiChat = {
	source: ChatSource.USER | ChatSource.GEMINI;
	message: string;
};

const GeminiChat = () => {
	const [loading, setLoading] = useState(false);
	const [apiData, setApiData] = useState<Array<GeminiChat>>([]);
	const [promptRequest, setPromptRequest] = useState<string>("");

	const { setShowAIWindow, showAIWindow } = useGeminiChat();

	useEffect(() => {
		if (!showAIWindow) {
			setApiData([]);
			return;
		}
		setApiData([
			{
				source: ChatSource.GEMINI,
				message: "Let me assist you today!",
			},
		]);
	}, [showAIWindow]);

	const genAI = new GoogleGenerativeAI(GEMINI_API_KEY);

	const fetchData = async () => {
		const textArr = apiData;
		textArr.push({
			source: ChatSource.USER,
			message: promptRequest,
		});
		setApiData(textArr);
		const model = genAI.getGenerativeModel({ model: "gemini-pro" });
		const prompt = promptRequest;
		const result = await model.generateContent(prompt);
		const response = await result.response;
		const textResponse = response.text();
		const textResponseArr = apiData;
		textResponseArr.push({
			source: ChatSource.GEMINI,
			message: textResponse,
		});
		setApiData(textArr);
		setLoading(false);
	};

	const handleSubmit = (e: MouseEvent | KeyboardEvent<HTMLInputElement>) => {
		e.preventDefault();
		setLoading(true);
		fetchData();
		setPromptRequest("");
	};

	return (
		<div>
			{!showAIWindow && (
				<div className="fixed bottom-4 right-4 z-50">
					<Button
						variant="primary"
						rounded="full"
						classNameButton="h-12 block font-gilroy-semibold text-nowrap hidden"
						onClick={() => setShowAIWindow(true)}>
						<SparklesIcon className="w-5 h-5 mr-2" /> AI Assistant
					</Button>
				</div>
			)}
			<Drawer
				className={"bg-slate-50"}
				setIsOpen={() => setShowAIWindow(!showAIWindow)}
				size="xl"
				isOpen={showAIWindow}>
				<div className="container fixed flex-col justify-between h-screen p-4">
					<div className="font-bold text-xl mb-4 flex flex-row">
						Opus AI Assistant
					</div>
					<div className="flex flex-col flex-grow h-[82%] overflow-y-auto hide-scrollbar">
						{apiData &&
							apiData.map((value, index) => {
								return (
									<div
										key={index}
										className="mb-3 flex flex-row justify-start align-top gap-3">
										{value.source === ChatSource.GEMINI && (
											<SparklesIcon className="w-5 h-5" />
										)}
										{value.source === ChatSource.USER && (
											<UserIcon className="w-5 h-5" />
										)}
										<div className="w-[85%]">
											<Markdown remarkPlugins={[remarkGfm]}>
												{value.message}
											</Markdown>
										</div>
									</div>
								);
							})}
						{loading && (
							<div className="font-normal text-opus-gray-200 text-sm">
								Generating reponse...
							</div>
						)}
					</div>
					<div className="mt-4 flex flex-row gap-4 align-middle">
						<Input
							name="promptRequest"
							placeholder="How may I assist you today?"
							autoComplete="off"
							autoFocus={true}
							value={promptRequest}
							onKeyDown={(e) => {
								if (e.key === "Enter") handleSubmit(e);
							}}
							onChange={(e) => setPromptRequest(e.target.value)}
						/>
					</div>
				</div>
			</Drawer>
		</div>
	);
};

export default GeminiChat;
